import { Attachment, Note, Report, ReportContent, Section } from 'api/types'
import { IconName } from 'components/Icon'

export const SECTION_ICONS: Record<Section, IconName> = {
  EQUIPMENT: 'truck',
  LAB_TESTS: 'list',
  MATERIALS: 'box',
  STAFF: 'users',
  SUPERVISION: 'clipboardCheck',
  VISITS: 'mapPin',
  WORKS: 'edit',
}

const toCount = (value: unknown) => {
  const parsed = Math.floor(Number(value))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

const trimmed = (value?: string | null) => (value ?? '').trim()

/** Drops blank/incomplete lines so "filled" checks reflect real content, not stray empty rows. */
export const normalize = (content: ReportContent): ReportContent => ({
  equipment: content.equipment
    .map((line) => ({ count: toCount(line.count), id: line.id, name: trimmed(line.name) }))
    .filter((line) => line.name),
  labTests: content.labTests
    .map((line) => ({
      id: line.id,
      location: trimmed(line.location) || null,
      result: trimmed(line.result) || null,
      testType: trimmed(line.testType),
    }))
    .filter((line) => line.testType || line.location || line.result),
  materials: content.materials
    .map((line) => ({ id: line.id, name: trimmed(line.name), quantity: trimmed(line.quantity) || null }))
    .filter((line) => line.name),
  staff: content.staff
    .map((line) => ({ count: toCount(line.count), id: line.id, name: trimmed(line.name) || null, title: trimmed(line.title) }))
    .filter((line) => line.title && (line.count > 0 || line.name)),
  visits: content.visits
    .map((line) => ({ id: line.id, party: trimmed(line.party), reason: trimmed(line.reason) || null }))
    .filter((line) => line.party || line.reason),
  weather: trimmed(content.weather) || null,
  works: content.works.map((line) => ({ id: line.id, text: trimmed(line.text) })).filter((line) => line.text),
})

export const contentOf = (report: Report): ReportContent => ({
  equipment: report.equipment,
  labTests: report.labTests,
  materials: report.materials,
  staff: report.staff,
  visits: report.visits,
  weather: report.weather,
  works: report.works,
})

/**
 * Display-only submit requirements. `label` stays the Arabic source text and is not saved on the report.
 * Render it with `t(item.label, item.labelEn)` inside the component so the language can still switch.
 */
export const submitChecklist = (content: ReportContent, attachments: Attachment[]) => {
  const attached = new Set(attachments.filter((item) => item.lineId).map((item) => `${item.section}:${item.lineId}`))
  return [
    { done: Boolean(content.weather), label: 'حالة الجو', labelEn: 'Weather' },
    { done: content.staff.some((line) => line.count > 0), label: 'بند كوادر واحد على الأقل مع العدد', labelEn: 'At least one crew line with a count' },
    { done: content.works.length > 0, label: 'وصف الأعمال', labelEn: 'Work description' },
    {
      done: content.labTests.every((line) => attached.has(`LAB_TESTS:${line.id}`)),
      label: 'مرفق نتيجة لكل فحص مخبري',
      labelEn: 'A result attachment for every lab test',
    },
    {
      done: content.materials.every((line) => attached.has(`MATERIALS:${line.id}`)),
      label: 'سند توريد أو صورة لكل مادة',
      labelEn: 'A delivery note or photo for every material',
    },
  ]
}

export interface SectionFillState {
  id: string
  section: Section
  filled: boolean
}

/** Per-section completeness for the section nav / sidebar checklist, based on normalized content. */
export const sectionFillStates = (normalized: ReportContent, notes: Note[]): SectionFillState[] => [
  { filled: normalized.staff.length > 0, id: 'sec-staff', section: 'STAFF' },
  { filled: normalized.labTests.length > 0, id: 'sec-lab', section: 'LAB_TESTS' },
  { filled: normalized.materials.length > 0, id: 'sec-materials', section: 'MATERIALS' },
  { filled: normalized.equipment.length > 0, id: 'sec-equipment', section: 'EQUIPMENT' },
  { filled: normalized.works.length > 0, id: 'sec-works', section: 'WORKS' },
  { filled: normalized.visits.length > 0, id: 'sec-visits', section: 'VISITS' },
  { filled: notes.some((note) => note.section === 'SUPERVISION'), id: 'sec-notes', section: 'SUPERVISION' },
]
